// packages
import defaults from 'underscore/modules/defaults.js';

const DEFAULT_EASING_FUNCTION = (t) => t;

/*
 * ScrollScene
 *
 * This creates an individual scene, which progresses based on
 * the DOM element's position relative to the viewport.
 *
 */
export class ScrollScene {
  constructor(elementOrId, options = {}) {
    // Options include:
    // sceneStart, sceneEnd (Number)
    //   This is the position relative to the elementID's start or end
    //   position when the scene should begin or end. It's measured in
    //   viewport height, where 1 is a full viewport's height.
    //
    //   The default values (1,1) indicate that the scene begins once the top
    //   of the element reaches the top of the viewport and ends once the
    //   bottom of the reaches the bottom of the viewport.
    //
    //   Some other examples:
    //
    //   (0, 1)   - Begins at one pixel of visibility and ends when all are
    //            visible.
    //   (0, 0)   - Begins at one pixel and ends once the last pixel is gone.
    //   (0.5, 1) - Begins when element is halfway on screen and ends when it
    //            is fully on screen
    //
    //   This certainly could be more intuitive!
    const fullOptions = defaults(options, {
      sceneStart: 1,
      sceneEnd: 1,
      easingFunction: DEFAULT_EASING_FUNCTION,
    });

    let sceneElement;

    if (typeof elementOrId === 'string') {
      sceneElement = document.getElementById(elementOrId.replace('#', ''));
    } else {
      sceneElement = elementOrId;
    }

    this.sceneElement = sceneElement;
    this.states = {};
    this.listeners = {};
    this.options = fullOptions;
  }

  // Get position attributes for the scene. This is called by the controller
  // on every resize.
  getData() {
    const bounds = this.sceneElement.getBoundingClientRect();
    const sceneData = {};

    sceneData.top = bounds.top + window.scrollY;
    sceneData.height = bounds.height;
    sceneData.bottom = sceneData.top + sceneData.height;

    // Where the scene's DOM element enters the viewport
    sceneData.visibilityPoint = sceneData.top - window.innerHeight;

    // Where the scene begins relative to the document
    sceneData.startPoint =
      sceneData.visibilityPoint + window.innerHeight * this.options.sceneStart;

    // Where the scene ends relative to the document
    sceneData.endPoint =
      sceneData.bottom - window.innerHeight * this.options.sceneEnd;

    // Total scroll distance of the scene
    sceneData.distance = sceneData.endPoint - sceneData.startPoint;

    // Point at which the element should start loading any needed assets
    const loadMargin = 2 * window.innerHeight;
    sceneData.topLoadPoint = sceneData.startPoint - loadMargin;
    sceneData.bottomLoadPoint = sceneData.endPoint + loadMargin;

    return sceneData;
  }

  ease(t) {
    return this.options.easingFunction(t);
  }

  // Register scroll events where the user has provided callbacks.
  // Supported events: start, end, progress, visible, notvisible
  on(eventName, callback) {
    this.listeners[eventName] = callback;
    return this;
  }

  // Helper function to call an event callback with arbitrary number of arguments
  _fireIfUserDefined(eventName, ...args) {
    if (this.listeners[eventName]) {
      this.listeners[eventName](...args);
    }
  }

  // Noop functions for subclass use
  progress() {}
  start() {}
  end() {}
  visible() {}
  notvisible() {}
  load() {}
  unload() {}

  _onProgress(progress) {
    if (this.states['last-progress'] !== progress) {
      this.states['last-progress'] = progress;
      this.progress(progress);
      this._fireIfUserDefined('progress', progress);
    }
  }

  _onSceneBegin(progress) {
    if (this.states['scene-started'] !== 'started') {
      this.states['scene-started'] = 'started';
      this.start(progress);
      this._fireIfUserDefined('start', progress);
    }
  }

  _onSceneEnd(progress) {
    if (this.states['scene-started'] !== 'ended') {
      this.states['scene-started'] = 'ended';
      this.end(progress);
      this._fireIfUserDefined('end', progress);
    }
  }

  _onElementEnter(progress) {
    if (this.states['visible'] !== 'visible') {
      this.states['visible'] = 'visible';
      this.visible(progress);
      this._fireIfUserDefined('visible', progress);
    }
  }

  _onElementLeave(progress) {
    if (this.states['visible'] !== 'notvisible') {
      this.states['visible'] = 'notvisible';
      this.notvisible(progress);
      this._fireIfUserDefined('notvisible', progress);
    }
  }

  _onLoad(progress) {
    if (this.states['loaded'] !== 'loaded') {
      this.states['loaded'] = 'loaded';
      this.load(progress);
      this._fireIfUserDefined('load', progress);
    }
  }

  _onUnload(progress) {
    if (this.states['loaded'] && this.states['loaded'] !== 'unloaded') {
      this.states['loaded'] = 'unloaded';
      this.load(progress);
      this._fireIfUserDefined('unload', progress);
    }
  }
}
